// eslint-disable-react/prop-types
import { useState, useMemo, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Column } from "react-table";
import Button from "components/atoms/Button/Button";

import { getCompanyAddressV2Book } from "api";
import { AddressV2 } from "api/models";
import testIds from "constants/testIds";
import AddressV2Dialog from "components/organisms/AddressV2Dialog/AddressV2Dialog";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import usePermissions from "hooks/usePermissions";
import useTableRequest from "hooks/useTableRequest";
import LocationFilter from "components/molecules/LocationFilter/LocationFilter";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import { classNames } from "helpers/commonHelpers";
import MissingGeolocationIndicator from "./MissingGeolocationIndicator";

export default function AddressV2Table() {
  const intl = useIntl();

  const { permissions } = usePermissions();
  const { company_shipper_settings, permissions: parsedPermissions } = JSON.parse(permissions ?? "{}");
  const { address_availability, co2_emission_enabled } = company_shipper_settings;
  const { restricted_to_user_address } = parsedPermissions?.quoting ?? {};

  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressV2>();

  const columns = useMemo<Column<AddressV2>[]>(
    () => [
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="tableHeaders.companyNameReference" defaultMessage="Company name / Reference #" />
          </span>
        ),
        sortable: false,
        id: "company_details",
        maxWidth: 150,
        Cell: ({ row }: { row: { original: AddressV2 } }) => {
          const { name, location_reference_number, custom_address_reference, is_geocoded } = row.original;
          return (
            <div className="text-wrap wrap-break-word">
              <div className="d-flex align-items-start gap-xxs">
                <p className="font-b4 mb-0">{name}</p>
                {co2_emission_enabled && is_geocoded === false && <MissingGeolocationIndicator />}
              </div>
              <p className="font-b3 text-secondary mb-0">
                {location_reference_number}
                {location_reference_number && custom_address_reference ? " | " : ""}
                {custom_address_reference}
              </p>
            </div>
          );
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.address" defaultMessage="Address" />
          </span>
        ),
        id: "address",
        sortable: false,
        maxWidth: 150,
        Cell: ({ row }: { row: { original: AddressV2 } }) => {
          const { street_address, street_address_line_two } = row.original;
          return (
            <p className="font-b5 mb-0 text-wrap">
              {street_address}
              <br />
              {street_address_line_two}
            </p>
          );
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.postalCode" defaultMessage="ZIP/Postal Code" />
          </span>
        ),
        accessor: "zipcode",
        id: "zipcode",
        sortable: false,
        Cell: ({ value }: { value: string }) => <p className="font-b5 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.city" defaultMessage="City" />
          </span>
        ),
        accessor: "city_name",
        id: "city_name",
        sortable: true,
        Cell: ({ value }: { value: string }) => <p className="font-b5 mb-0 text-wrap">{value}</p>,
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.state" defaultMessage="State" />
          </span>
        ),
        accessor: "state_name",
        id: "state_name",
        sortable: true,
        Cell: ({ value }: { value: string }) => <p className="font-b5 mb-0 text-wrap">{value}</p>,
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.country" defaultMessage="Country" />
          </span>
        ),
        accessor: "country_name",
        id: "country_name",
        sortable: true,
        Cell: ({ value }: { value: string }) => <p className="font-b5 mb-0 text-wrap">{value}</p>,
      },
      {
        Header: <span className="text-nowrap">UN/LOCODE</span>,
        accessor: "un_locode",
        id: "locode",
        sortable: true,
        Cell: ({ value }: { value: string }) => <p className="font-b5 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.lastModified" defaultMessage="Last modified" />
          </span>
        ),
        id: "last_modified",
        sortable: false,
        maxWidth: 150,
        Cell: ({ row }: { row: { original: AddressV2 } }) => {
          const { last_modified } = row.original;
          return <p className="font-b5 mb-0 text-wrap">{last_modified}</p>;
        },
      },
    ],
    [intl, co2_emission_enabled],
  );

  const { instance, fetching, totalData, refetch } = useTableRequest(getCompanyAddressV2Book, columns, {
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
      ...(globalFilter.geocoded?.length && { geocoded: globalFilter.geocoded }),
    }),
  });

  useEffect(() => {
    if (!instance.state.globalFilter?.payment_location) {
      instance.setGlobalFilter({
        ...instance.state.globalFilter,
        payment_location: "all",
      });
    }
  }, [instance]);

  const openAddAddressDialog = () => {
    setSelectedAddress(undefined);
    setShowAddressDialog(true);
  };

  const openEditAddressDialog = (address: AddressV2) => {
    setSelectedAddress(address);
    setShowAddressDialog(true);
  };

  const closeEditAddressDialog = () => {
    setSelectedAddress(undefined);
    setShowAddressDialog(false);
  };

  const handleFilterSelect = (value: string) => {
    instance.setGlobalFilter({
      ...instance.state.globalFilter,
      payment_location: value || "all",
    });
  };

  const handleGeocodedFilterSelect = (value: string) => {
    instance.setGlobalFilter({
      ...instance.state.globalFilter,
      geocoded: value,
    });
  };

  return (
    <>
      <AddressV2Dialog
        selectedAddress={selectedAddress}
        onClose={closeEditAddressDialog}
        isOpen={showAddressDialog}
        refetchAddresses={refetch}
        source="address_book"
        formSelectedShipperLocation={
          instance.state.globalFilter?.payment_location === "all" ? null : instance.state.globalFilter?.payment_location
        }
      />
      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => openEditAddressDialog(row.original)}
        headerRight={
          <div className="d-flex flex-column mt-3 w-100">
            <div
              className={classNames("d-flex justify-content-between w-100", {
                "mb-md": co2_emission_enabled,
              })}
            >
              <div className="d-flex align-items-center gap-sm">
                <GlobalTableSearch
                  currentFilters={instance.state.globalFilter}
                  setFilters={instance.setGlobalFilter}
                  filterKey="query"
                  eventContext="Address Book"
                />
                {address_availability === "Payment Location" && !restricted_to_user_address && (
                  <div className="d-flex align-items-center">
                    <LocationFilter onChange={handleFilterSelect} />
                  </div>
                )}
              </div>

              <Button
                className="ml-3 rounded-pill"
                onClick={openAddAddressDialog}
                data-testid={testIds.ADDRESS_BOOK_ADD_BUTTON}
              >
                <FormattedMessage id="settings.address-book.addAddress" defaultMessage="Add address" />
              </Button>
            </div>
            {co2_emission_enabled && (
              <PillTabs
                id="address-book-geocoded-tabs"
                activeKey={instance.state.globalFilter?.geocoded ?? ""}
                onSelect={(eventKey: string | null) => handleGeocodedFilterSelect(eventKey || "")}
                isResponsive={false}
                tabItems={[
                  {
                    eventKey: "",
                    title: intl.formatMessage({
                      id: "settings.address-book.geocodedFilter.all",
                      defaultMessage: "All",
                    }),
                    testId: testIds.ADDRESS_BOOK_GEOCODED_TAB_ALL,
                  },
                  {
                    eventKey: "yes",
                    title: intl.formatMessage({
                      id: "settings.address-book.geocodedFilter.geocoded",
                      defaultMessage: "Geocoded",
                    }),
                    testId: testIds.ADDRESS_BOOK_GEOCODED_TAB_GEOCODED,
                  },
                  {
                    eventKey: "no",
                    title: intl.formatMessage({
                      id: "settings.address-book.geocodedFilter.uncoded",
                      defaultMessage: "Uncoded",
                    }),
                    testId: testIds.ADDRESS_BOOK_GEOCODED_TAB_UNCODED,
                  },
                ]}
              />
            )}
          </div>
        }
      />
    </>
  );
}
